import { LegalLayout, LegalSection } from "../component/legal/LegalLayout";
import { siteConfig } from "../config/site";

function ContactBlock({
  label,
  name,
  phone,
  email,
  complaintEmail,
}: {
  label: string;
  name?: string;
  phone?: string;
  email?: string;
  complaintEmail?: string;
}) {
  if (!name && !phone && !email) return null;
  return (
    <div className="rounded-xl bg-[#f8fafc] p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.4px] text-brand-teal">
        {label}
      </p>
      {name ? <p className="pt-1 font-semibold text-[#1c1917]">{name}</p> : null}
      {phone ? <p className="text-[#475569]">{phone}</p> : null}
      {email ? <p className="text-[#475569]">{email}</p> : null}
      {complaintEmail ? (
        <p className="text-[#475569]">{complaintEmail}</p>
      ) : null}
    </div>
  );
}

export function GrievanceRedressalPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Grievance Redressal Policy"
      description={`How ${siteConfig.companyName} resolves customer complaints and grievances.`}
    >
      <LegalSection heading="Preface">
        <p>
          {siteConfig.companyName} (the "Company") is committed to giving
          due consideration to customer grievances and taking quick action
          to resolve them. This Grievance Redressal Policy is framed in
          terms of applicable Reserve Bank of India directions, as amended
          from time to time, and provides for an effective Grievance
          Redressal Mechanism.
        </p>
        <p>
          This policy outlines the resolution timeline and escalation
          matrix for the smooth disposal of complaints, and lays down the
          Company's responsibility to ensure that all grievances are
          resolved within 30 days from the date of raising the complaint. If
          the grievance is not resolved within 30 days, or the customer
          remains unsatisfied with the resolution, the customer may approach
          the Reserve Bank of India for further resolution.
        </p>
      </LegalSection>

      <LegalSection heading="Objective">
        <p>
          This policy shall apply to complaints arising out of actions of
          the Company's agents, recovery agents, digital lending partners,
          and other service providers, who are bound by the following
          commitments:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Offering efficient channels to route customer queries.</li>
          <li>
            Complaints raised by customers shall be dealt with courtesy and
            resolved in a timely manner.
          </li>
          <li>
            Customers shall be informed of avenues to escalate their
            complaints within the organization, and their rights when
            complaints are not resolved in a timely manner or they are not
            satisfied with the resolution.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="Grievance Redressal Mechanism">
        <p>
          Any customer having a grievance, complaint, or feedback with
          respect to the service offered by the Company may write to the
          Company as follows:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          {siteConfig.email ? (
            <li>
              Customers with a grievance can send their grievance via email
              at{" "}
              <a
                href={`mailto:${siteConfig.email}`}
                className="font-semibold text-brand-purple underline"
              >
                {siteConfig.email}
              </a>
              .
            </li>
          ) : null}
          {siteConfig.complaintEmail ? (
            <li>
              Complaints can also be sent via email to{" "}
              <a
                href={`mailto:${siteConfig.complaintEmail}`}
                className="font-semibold text-brand-purple underline"
              >
                {siteConfig.complaintEmail}
              </a>
              .
            </li>
          ) : null}
          {siteConfig.address ? (
            <li>
              The customer may also send letters to the registered office of
              the Company at {siteConfig.address}.
            </li>
          ) : null}
          <li>
            For proper resolution, customers are requested to mention the
            loan application number or loan account number while filing the
            complaint, along with their PAN number.
          </li>
          <li>
            Anonymous complaints may be examined at the discretion of the
            Company, depending on the nature and seriousness of the issue
            raised.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="Resolution Process">
        <div className="flex flex-col gap-4">
          <div>
            <p className="font-semibold text-[#1c1917]">Level 1</p>
            <p>
              The customer may lodge a complaint by calling our customer
              care team{siteConfig.phoneDisplay ? ` at ${siteConfig.phoneDisplay}` : ""}
              {siteConfig.email ? `, emailing ${siteConfig.email},` : ""}
              {siteConfig.complaintEmail ? ` or emailing ${siteConfig.complaintEmail}` : ""}.
              The complaint shall be allotted to a Customer Service
              Executive, who will send an acknowledgement within 24 hours of
              receipt and ensure resolution within 7 working days.
            </p>
          </div>
          <div>
            <p className="font-semibold text-[#1c1917]">Level 2</p>
            <p>
              If the customer is not satisfied with the resolution provided
              at Level 1, they may escalate their complaint to the Grievance
              Redressal Officer, who will provide a resolution within 7
              working days of receipt.
            </p>
          </div>
          <div>
            <p className="font-semibold text-[#1c1917]">Level 3</p>
            <p>
              If the customer remains unsatisfied, they may escalate their
              complaint to the Nodal Officer, who will provide a resolution
              within 15 working days of receipt. The Company ensures that
              all complaints are resolved within 30 days from the date of
              raising the complaint.
            </p>
          </div>
        </div>
      </LegalSection>

      <LegalSection heading="Escalation Matrix">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ContactBlock
            label="First Escalation — Customer Care"
            phone={siteConfig.phoneDisplay}
            email={siteConfig.email}
            complaintEmail={siteConfig.complaintEmail}
          />
          <ContactBlock
            label="Second Escalation — Grievance Officer"
            name={siteConfig.grievance.officerName}
            phone={siteConfig.grievance.officerPhone}
            email={siteConfig.grievance.officerEmail}
          />
          <ContactBlock
            label="Third Escalation — Nodal Officer"
            name={siteConfig.grievance.nodalName}
            phone={siteConfig.grievance.nodalPhone}
            email={siteConfig.grievance.nodalEmail}
          />
        </div>
      </LegalSection>

      <LegalSection heading="Review of the Policy">
        <p>
          The Board shall review and amend this policy as and when required.
          If at any point a conflict of interpretation or information
          arises between this policy and any regulations, rules, guidelines,
          notifications, clarifications, or circulars issued by relevant
          authorities ("Regulatory Provisions"), the interpretation of the
          Regulatory Provisions shall prevail. In case of any amendment or
          clarification to the Regulatory Provisions, this policy shall
          stand amended accordingly from the effective date specified by the
          Regulatory Provisions.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
